/**
 * Mock Portfolio Update Repository
 * Returns sample data for development
 */

import type {
  IPortfolioUpdateRepository,
  PortfolioUpdate,
  CreatePortfolioUpdateData,
} from '../interfaces/IPortfolioUpdateRepository'
import type { CursorParams, PaginatedResult } from '@/lib/types/pagination'

const mockUpdates: PortfolioUpdate[] = [
  {
    id: '1',
    userId: 'admin-1',
    username: 'admin',
    displayName: 'Sovereign Admin',
    portfolioValue: 1769538.93,
    positions:
      'USD — $1,618,342.40 (91.46%)\nGEV — 118 shares @ $846.99 (5.64%)\nVRT — 191 shares @ $261.64 (2.90%)',
    commentary:
      'Portfolio holding steady. GEV position slightly down, VRT showing positive momentum.',
    createdAt: new Date().toISOString(),
  },
]

export class MockPortfolioUpdateRepository implements IPortfolioUpdateRepository {
  private updates = [...mockUpdates]

  async getUpdates(): Promise<PortfolioUpdate[]> {
    await new Promise((r) => setTimeout(r, 300))
    return this.updates
  }

  async getUpdatesPaginated(
    params: Pick<CursorParams, 'cursor' | 'cursorId' | 'limit'>
  ): Promise<PaginatedResult<PortfolioUpdate>> {
    await new Promise((r) => setTimeout(r, 200))

    let items = [...this.updates].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    // Apply cursor
    if (params.cursor && params.cursorId) {
      const cursorDate = new Date(params.cursor).getTime()
      items = items.filter((u) => {
        const itemDate = new Date(u.createdAt).getTime()
        if (itemDate < cursorDate) return true
        if (itemDate === cursorDate && u.id < params.cursorId!) return true
        return false
      })
    }

    const hasMore = items.length > params.limit
    const page = items.slice(0, params.limit)
    const last = page[page.length - 1]

    return {
      data: page,
      nextCursor: last ? last.createdAt : null,
      nextCursorId: last ? last.id : null,
      hasMore,
    }
  }

  async createUpdate(
    data: CreatePortfolioUpdateData
  ): Promise<PortfolioUpdate> {
    await new Promise((r) => setTimeout(r, 300))
    const update: PortfolioUpdate = {
      id: crypto.randomUUID(),
      userId: data.userId,
      username: 'admin',
      displayName: 'Sovereign Admin',
      screenshotUrl: data.screenshotUrl,
      portfolioValue: data.portfolioValue,
      positions: data.positions,
      commentary: data.commentary,
      createdAt: new Date().toISOString(),
    }
    this.updates.unshift(update)
    return update
  }

  async deleteUpdate(id: string): Promise<void> {
    await new Promise((r) => setTimeout(r, 200))
    this.updates = this.updates.filter((u) => u.id !== id)
  }
}
